import React, { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { RootState } from '../../store';
import { cn } from '@/lib/utils';
import {
    Tooltip,
    TooltipContent,
    TooltipPortal,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import NotificationBadge from './NotificationBadge';
import {
    BROWSE_NAV_ITEMS,
    getProfileNavItem,
    getRoleNavItems,
    isNavItemActive,
    type NavItem,
} from './navConfig';
import { layoutRail, railIconPx, railVisibleIds } from './railLayout';
import { usePublishRailVisible } from './railVisible';

const ACTIVE_PILL_TRANSITION = { type: 'spring', stiffness: 500, damping: 30 } as const;

interface SideNavLinkProps {
    item: NavItem
    isActive: boolean
    badgeCount?: number
}

const SideNavLinkBase: React.FC<SideNavLinkProps> = ({ item, isActive, badgeCount = 0 }) => {
    const { Icon, label, to, strokeWidth } = item;

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Link
                    to={to}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                        'group relative flex shrink-0 items-center justify-center rounded-full p-[var(--rail-pad)] outline-none transition-colors',
                        'focus-visible:ring-2 focus-visible:ring-purple-light focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-100 dark:focus-visible:ring-jw-wine-text dark:focus-visible:ring-offset-jw-chrome',
                        isActive
                            ? item.restricted
                                ? 'text-white hover:!text-white dark:text-amber-300'
                                : 'text-white'
                            : item.restricted
                              ? 'text-amber-800 ring-1 ring-inset ring-amber-500 hover:bg-amber-50 dark:text-amber-300 dark:hover:!text-white dark:hover:bg-amber-950/40'
                              : 'text-gray-500 hover:text-gray-700 dark:text-jw-ink-2 dark:hover:!text-white'
                    )}
                >
                    {isActive && (
                        <motion.div
                            className={cn(
                                'absolute inset-0 rounded-full',
                                item.restricted ? 'bg-amber-700 dark:bg-amber-950/60' : 'bg-purple-light dark:bg-jw-wine'
                            )}
                            layoutId="activeNavBackground"
                            initial={false}
                            transition={ACTIVE_PILL_TRANSITION}
                        />
                    )}
                    <Icon
                        className={cn('relative z-10', item.restricted && 'dark:group-hover:!text-white')}
                        size={railIconPx(item.id)}
                        strokeWidth={strokeWidth}
                        aria-hidden="true"
                    />
                    <span className="sr-only">{label}</span>
                    <NotificationBadge count={badgeCount} className="absolute right-1 top-1 z-20" />
                </Link>
            </TooltipTrigger>
            {/* Portaled so the label is not trapped under the fixed rail's stacking context. */}
            <TooltipPortal>
                <TooltipContent side="right" sideOffset={8} chrome>
                    {label}
                </TooltipContent>
            </TooltipPortal>
        </Tooltip>
    );
};

// Memoized so a route or badge change only re-renders the links whose props changed.
const SideNavLink = memo(SideNavLinkBase);

const SideNav: React.FC = () => {
    const user = useSelector((state: RootState) => state.auth.user);
    const unreadCount = useSelector((state: RootState) => state.notifications.unreadCount);
    const { pathname } = useLocation();

    const accountItems = useMemo(
        () => (user ? [getProfileNavItem(user), ...getRoleNavItems(user)] : []),
        [user]
    );

    const allItems = useMemo(() => [...BROWSE_NAV_ITEMS, ...accountItems], [accountItems]);
    const navRef = useRef<HTMLElement>(null);
    const [boxHeight, setBoxHeight] = useState(0);

    // The nav fills the aside, so its box is the space the icons can actually use.
    useLayoutEffect(() => {
        const nav = navRef.current;
        if (!nav || typeof ResizeObserver === 'undefined') return undefined;

        const publish = () => {
            const next = nav.clientHeight;
            setBoxHeight((prev) => (Math.abs(prev - next) < 0.5 ? prev : next));
        };

        publish();
        const observer = new ResizeObserver(publish);
        observer.observe(nav);
        return () => observer.disconnect();
    }, [user]);

    const layout = useMemo(() => layoutRail(allItems, boxHeight), [allItems, boxHeight]);
    const publishRail = usePublishRailVisible();

    // The menu hides a link only after the rail says that icon is on screen. Publishing before
    // paint keeps the first frame from dropping a destination in both places.
    useLayoutEffect(() => {
        publishRail(railVisibleIds(allItems, user ? boxHeight : 0));
    }, [allItems, boxHeight, publishRail, user]);

    const railStyle = useMemo(() => ({
        gap: layout.gap,
        paddingTop: layout.navPad,
        paddingBottom: layout.navPad,
        '--rail-pad': `${layout.pad}px`,
    }) as React.CSSProperties, [layout]);

    // Every rail destination needs a session, so signed-out visitors get no rail.
    if (!user) return null;

    const renderItem = (item: NavItem) => (
        <SideNavLink
            key={item.id}
            item={item}
            isActive={isNavItemActive(pathname, item)}
            badgeCount={item.id === 'notifications' ? unreadCount : 0}
        />
    );

    return (
        <nav ref={navRef} aria-label="Main" className="flex min-h-0 w-full flex-1 flex-col items-center justify-center" style={railStyle}>
            <TooltipProvider delayDuration={200}>
                {layout.items.map(renderItem)}
            </TooltipProvider>
        </nav>
    );
};

export default SideNav;
